import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import {
  createYoutubeScript,
  createYoutubeScriptVersionFromDraft,
  deleteYoutubeScript,
  generateYoutubeScript,
  getYoutubeScript,
  listYoutubeScriptTemplates,
  listYoutubeScripts,
  regenerateYoutubeScriptSection,
  restoreYoutubeScriptVersion,
  saveYoutubeScriptDraft,
  updateYoutubeScript
} from '../../services/contentAiService';

function safeJsonParse(text) {
  try {
    const v = JSON.parse(String(text || ''));
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

function prettyJson(v) {
  try {
    return JSON.stringify(v || {}, null, 2);
  } catch {
    return '{}';
  }
}

function normalizeHashtags(text) {
  const parts = String(text || '')
    .split(/[, \n]+/g)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.startsWith('#') ? s : `#${s}`));
  const uniq = [];
  for (const p of parts) {
    const k = p.toLowerCase();
    if (!uniq.some((u) => u.toLowerCase() === k)) uniq.push(p);
  }
  return uniq;
}

function secondsToHms(seconds) {
  const s = Math.max(0, Number(seconds || 0) || 0);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  if (hh > 0) return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export default function YouTubeScriptsPage() {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [scriptRow, setScriptRow] = useState(null);
  const [versions, setVersions] = useState([]);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createTopic, setCreateTopic] = useState('');
  const [createTemplateKey, setCreateTemplateKey] = useState('documentario');
  const [createLanguage, setCreateLanguage] = useState('pt-BR');
  const [createTone, setCreateTone] = useState('profissional');
  const [createNarrativeStyle, setCreateNarrativeStyle] = useState('direto');
  const [createConstraints, setCreateConstraints] = useState('{}');
  const [createMinutes, setCreateMinutes] = useState(8);
  const [createSeconds, setCreateSeconds] = useState(45);

  const [editOpen, setEditOpen] = useState(false);
  const [editTopic, setEditTopic] = useState('');
  const [editTemplateKey, setEditTemplateKey] = useState('documentario');
  const [editLanguage, setEditLanguage] = useState('pt-BR');
  const [editTone, setEditTone] = useState('');
  const [editNarrativeStyle, setEditNarrativeStyle] = useState('');
  const [editConstraints, setEditConstraints] = useState('{}');

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [regenSection, setRegenSection] = useState('title');
  const [regenInstruction, setRegenInstruction] = useState('');

  const draftSaveTimer = useRef(null);
  const lastDraftSentRef = useRef('');

  const selectedLabel = useMemo(() => {
    const t = templates.find((x) => x.key === createTemplateKey);
    return t?.label || createTemplateKey;
  }, [createTemplateKey, templates]);

  const loadTemplates = async () => {
    try {
      const resp = await listYoutubeScriptTemplates();
      const data = resp?.data || [];
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar templates');
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const resp = await listYoutubeScripts(100);
      const data = resp?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar roteiros');
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (id) => {
    setLoading(true);
    try {
      const resp = await getYoutubeScript(id);
      const data = resp?.data || null;
      setScriptRow(data?.script || null);
      setVersions(Array.isArray(data?.versions) ? data.versions : []);
      setDraft(data?.script?.draftJson || null);
      setSelectedId(id);
      setTab(0);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar roteiro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadList();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadOne(selectedId);
  }, [selectedId]);

  const openEdit = () => {
    if (!scriptRow) return;
    setEditTopic(scriptRow.topic || '');
    setEditTemplateKey(scriptRow.templateKey || 'documentario');
    setEditLanguage(scriptRow.language || 'pt-BR');
    setEditTone(scriptRow.tone || '');
    setEditNarrativeStyle(scriptRow.narrativeStyle || '');
    setEditConstraints(prettyJson(scriptRow.constraintsJson || {}));
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!selectedId) return;
    const topic = String(editTopic || '').trim();
    if (!topic) {
      toast.error('Tema obrigatório');
      return;
    }
    const constraintsJson = safeJsonParse(editConstraints || '');
    if (!constraintsJson) {
      toast.error('Restrições (JSON) inválidas');
      return;
    }
    setLoading(true);
    try {
      await updateYoutubeScript(selectedId, {
        topic,
        templateKey: editTemplateKey,
        language: editLanguage,
        tone: editTone || null,
        narrativeStyle: editNarrativeStyle || null,
        constraintsJson
      });
      toast.success('Roteiro atualizado');
      setEditOpen(false);
      await loadOne(selectedId);
      await loadList();
    } catch (e) {
      toast.error(e?.message || 'Falha ao atualizar roteiro');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await deleteYoutubeScript(selectedId);
      toast.success('Roteiro excluído');
      setDeleteOpen(false);
      setSelectedId(null);
      setScriptRow(null);
      setDraft(null);
      setVersions([]);
      await loadList();
    } catch (e) {
      toast.error(e?.message || 'Falha ao excluir roteiro');
    } finally {
      setLoading(false);
    }
  };

  const createNew = async (alsoGenerate) => {
    const constraintsJson = safeJsonParse(createConstraints);
    if (!createTopic.trim()) {
      toast.error('Informe o tema');
      return;
    }
    if (!constraintsJson) {
      toast.error('constraintsJson inválido');
      return;
    }
    setLoading(true);
    try {
      const resp = await createYoutubeScript({
        templateKey: createTemplateKey,
        topic: createTopic.trim(),
        language: createLanguage,
        tone: createTone,
        narrativeStyle: createNarrativeStyle,
        constraintsJson
      });
      const created = resp?.data || null;
      if (!created?.id) throw new Error('Falha ao criar roteiro');
      setCreateOpen(false);
      setCreateTopic('');
      await loadList();
      await loadOne(created.id);
      toast.success(`Roteiro criado (${selectedLabel})`);
      if (alsoGenerate) {
        await generateNow(created.id);
        await loadOne(created.id);
      }
    } catch (e) {
      toast.error(e?.message || 'Falha ao criar roteiro');
    } finally {
      setLoading(false);
    }
  };

  const generateNow = async (id) => {
    setLoading(true);
    try {
      await generateYoutubeScript(id, { minutes: Number(createMinutes) || 8, seconds: Number(createSeconds) || 45 });
      toast.success('Gerado via IA');
      await loadList();
    } catch (e) {
      toast.error(e?.message || 'Falha ao gerar');
    } finally {
      setLoading(false);
    }
  };

  const scheduleDraftSave = (nextDraft) => {
    if (!selectedId) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(async () => {
      const payloadStr = prettyJson(nextDraft);
      if (payloadStr === lastDraftSentRef.current) return;
      lastDraftSentRef.current = payloadStr;
      try {
        await saveYoutubeScriptDraft(selectedId, nextDraft);
      } catch (e) {
        toast.error(e?.message || 'Falha no auto-save');
      }
    }, 900);
  };

  const setDraftField = (path, value) => {
    const current = draft && typeof draft === 'object' ? draft : {};
    const next = { ...current };
    const parts = String(path).split('.');
    let obj = next;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const p = parts[i];
      if (!obj[p] || typeof obj[p] !== 'object') obj[p] = {};
      obj = obj[p];
    }
    obj[parts[parts.length - 1]] = value;
    setDraft(next);
    scheduleDraftSave(next);
  };

  const saveVersion = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await createYoutubeScriptVersionFromDraft(selectedId);
      toast.success('Versão salva');
      await loadOne(selectedId);
      await loadList();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar versão');
    } finally {
      setLoading(false);
    }
  };

  const regen = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await regenerateYoutubeScriptSection(selectedId, { section: regenSection, instruction: regenInstruction });
      toast.success('Seção regenerada');
      setRegenInstruction('');
      await loadOne(selectedId);
      await loadList();
    } catch (e) {
      toast.error(e?.message || 'Falha ao regenerar seção');
    } finally {
      setLoading(false);
    }
  };

  const restore = async (v) => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await restoreYoutubeScriptVersion(selectedId, v);
      toast.success('Versão restaurada');
      await loadOne(selectedId);
      await loadList();
    } catch (e) {
      toast.error(e?.message || 'Falha ao restaurar versão');
    } finally {
      setLoading(false);
    }
  };

  const chaptersText = useMemo(() => {
    if (!draft?.chapters) return '[]';
    return prettyJson(draft.chapters);
  }, [draft]);

  const seoKeywordsText = useMemo(() => {
    const ks = draft?.seo?.keywords;
    if (!Array.isArray(ks)) return '';
    return ks.join(', ');
  }, [draft]);

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h5">Roteiros YouTube (IA)</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" onClick={loadList} disabled={loading}>Atualizar</Button>
          <Button variant="contained" onClick={() => setCreateOpen(true)} disabled={loading}>Novo roteiro</Button>
        </Box>
      </Box>

      <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '420px 1fr' }}>
        <Box>
          <DataTable
            rows={rows}
            loading={loading}
            columns={[
              { field: 'id', headerName: 'ID', width: 80 },
              { field: 'templateKey', headerName: 'Template', width: 140 },
              { field: 'topic', headerName: 'Tema', flex: 1, minWidth: 220, valueGetter: (p) => String(p?.row?.topic || '').slice(0, 80) },
              { field: 'currentVersion', headerName: 'Versão', width: 90 }
            ]}
            getRowId={(r) => r.id}
            onRowClick={(row) => loadOne(row.id)}
          />
        </Box>

        <Box>
          {!selectedId ? (
            <Typography color="text.secondary">Selecione um roteiro para editar.</Typography>
          ) : (
            <Box display="grid" gap={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Box>
                  <Typography variant="h6">#{scriptRow?.id} {scriptRow?.topic}</Typography>
                  <Typography color="text.secondary">Template: {scriptRow?.templateKey} | Versão atual: {scriptRow?.currentVersion}</Typography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button variant="outlined" onClick={openEdit} disabled={loading || !scriptRow}>Editar</Button>
                  <Button variant="outlined" onClick={() => generateNow(selectedId)} disabled={loading}>Gerar via IA</Button>
                  <Button variant="contained" onClick={saveVersion} disabled={loading || !draft}>Salvar versão</Button>
                  <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)} disabled={loading || !scriptRow}>Excluir</Button>
                </Box>
              </Box>

              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Editor" />
                <Tab label="Preview" />
                <Tab label="Versões" />
              </Tabs>

              {tab === 0 ? (
                <Box display="grid" gap={2}>
                  {!draft ? (
                    <Typography color="text.secondary">Gere via IA para iniciar o draft editável.</Typography>
                  ) : (
                    <>
                      <TextField
                        label="Título"
                        value={draft.title || ''}
                        onChange={(e) => setDraftField('title', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="Descrição"
                        value={draft.description || ''}
                        onChange={(e) => setDraftField('description', e.target.value)}
                        fullWidth
                        multiline
                        minRows={5}
                      />
                      <TextField
                        label="Hashtags (separadas por espaço ou vírgula)"
                        value={Array.isArray(draft.hashtags) ? draft.hashtags.join(' ') : ''}
                        onChange={(e) => setDraftField('hashtags', normalizeHashtags(e.target.value))}
                        fullWidth
                      />
                      <TextField
                        label="CTA"
                        value={draft.cta || ''}
                        onChange={(e) => setDraftField('cta', e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                      <TextField
                        label="Duração estimada (segundos)"
                        value={draft.estimatedDurationSeconds || ''}
                        onChange={(e) => setDraftField('estimatedDurationSeconds', Number(e.target.value) || 0)}
                        type="number"
                        fullWidth
                      />
                      <TextField
                        label="SEO Keywords (separadas por vírgula)"
                        value={seoKeywordsText}
                        onChange={(e) => setDraftField('seo', { ...(draft.seo || {}), keywords: String(e.target.value || '').split(',').map(s => s.trim()).filter(Boolean) })}
                        fullWidth
                      />
                      <TextField
                        label="Chapters (JSON)"
                        value={chaptersText}
                        onChange={(e) => {
                          const parsed = safeJsonParse(e.target.value);
                          if (parsed) setDraftField('chapters', parsed);
                        }}
                        fullWidth
                        multiline
                        minRows={8}
                      />
                      <TextField
                        label="Texto de narração (completo)"
                        value={draft.narrationText || ''}
                        onChange={(e) => setDraftField('narrationText', e.target.value)}
                        fullWidth
                        multiline
                        minRows={12}
                      />

                      <Box display="grid" gap={1}>
                        <Typography variant="subtitle1">Regeneração parcial</Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                          <FormControl sx={{ minWidth: 220 }}>
                            <InputLabel>Seção</InputLabel>
                            <Select label="Seção" value={regenSection} onChange={(e) => setRegenSection(e.target.value)}>
                              <MenuItem value="title">Título</MenuItem>
                              <MenuItem value="description">Descrição</MenuItem>
                              <MenuItem value="hashtags">Hashtags</MenuItem>
                              <MenuItem value="cta">CTA</MenuItem>
                              <MenuItem value="chapters">Chapters</MenuItem>
                              <MenuItem value="narrationText">Narração</MenuItem>
                              <MenuItem value="script.hook">Hook</MenuItem>
                              <MenuItem value="script.intro">Intro</MenuItem>
                              <MenuItem value="script.body">Body</MenuItem>
                              <MenuItem value="script.outro">Outro</MenuItem>
                              <MenuItem value="seo">SEO</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            label="Instrução extra (opcional)"
                            value={regenInstruction}
                            onChange={(e) => setRegenInstruction(e.target.value)}
                            sx={{ flex: 1, minWidth: 280 }}
                          />
                          <Button variant="outlined" onClick={regen} disabled={loading || !draft}>Regenerar</Button>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              ) : null}

              {tab === 1 ? (
                <Box display="grid" gap={1}>
                  {!draft ? (
                    <Typography color="text.secondary">Sem draft para preview.</Typography>
                  ) : (
                    <>
                      <Typography variant="h6">{draft.title}</Typography>
                      <Typography color="text.secondary">Duração estimada: {secondsToHms(draft.estimatedDurationSeconds)} | Hashtags: {(draft.hashtags || []).join(' ')}</Typography>
                      <Typography mt={1} whiteSpace="pre-wrap">{draft.description}</Typography>
                      <Typography mt={2} variant="subtitle1">Chapters</Typography>
                      <Box component="ol" sx={{ pl: 3 }}>
                        {(draft.chapters || []).map((c, idx) => (
                          <li key={idx}>
                            <Typography><strong>{c.start}</strong> - {c.title}</Typography>
                            <Typography color="text.secondary">{c.summary}</Typography>
                          </li>
                        ))}
                      </Box>
                      <Typography mt={2} variant="subtitle1">Narração</Typography>
                      <Typography whiteSpace="pre-wrap">{draft.narrationText}</Typography>
                      <Typography mt={2} variant="subtitle1">CTA</Typography>
                      <Typography whiteSpace="pre-wrap">{draft.cta}</Typography>
                    </>
                  )}
                </Box>
              ) : null}

              {tab === 2 ? (
                <Box display="grid" gap={2}>
                  <Typography color="text.secondary">Restaurar cria uma nova versão ativa com o conteúdo restaurado.</Typography>
                  <DataTable
                    rows={versions}
                    loading={loading}
                    columns={[
                      { field: 'version', headerName: 'Versão', width: 90 },
                      { field: 'reason', headerName: 'Motivo', width: 140 },
                      { field: 'provider', headerName: 'Provider', width: 120 },
                      { field: 'model', headerName: 'Model', width: 180 },
                      { field: 'createdAt', headerName: 'Criado', width: 180, valueGetter: (p) => (p?.row?.createdAt ? new Date(p.row.createdAt).toLocaleString() : '') },
                      {
                        field: 'actions',
                        headerName: 'Ações',
                        width: 160,
                        sortable: false,
                        renderCell: (params) => (
                          <Button size="small" variant="outlined" onClick={() => restore(params.row.version)} disabled={loading}>
                            Restaurar
                          </Button>
                        )
                      }
                    ]}
                    getRowId={(r) => r.id}
                  />
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Novo roteiro</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField label="Tema" value={createTopic} onChange={(e) => setCreateTopic(e.target.value)} fullWidth sx={{ gridColumn: '1 / -1' }} />
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select label="Template" value={createTemplateKey} onChange={(e) => setCreateTemplateKey(e.target.value)}>
                {templates.map((t) => (
                  <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Idioma" value={createLanguage} onChange={(e) => setCreateLanguage(e.target.value)} fullWidth />
            <TextField label="Tom de voz" value={createTone} onChange={(e) => setCreateTone(e.target.value)} fullWidth />
            <TextField label="Estilo narrativo" value={createNarrativeStyle} onChange={(e) => setCreateNarrativeStyle(e.target.value)} fullWidth />
            <TextField label="Duração alvo (min)" type="number" value={createMinutes} onChange={(e) => setCreateMinutes(e.target.value)} fullWidth />
            <TextField label="Shorts: duração (seg)" type="number" value={createSeconds} onChange={(e) => setCreateSeconds(e.target.value)} fullWidth />
            <TextField
              label="Restrições (JSON)"
              value={createConstraints}
              onChange={(e) => setCreateConstraints(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCreateOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={() => createNew(false)} disabled={loading}>Criar</Button>
          <Button variant="contained" onClick={() => createNew(true)} disabled={loading}>Criar e gerar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar roteiro</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField label="Tema" value={editTopic} onChange={(e) => setEditTopic(e.target.value)} fullWidth sx={{ gridColumn: '1 / -1' }} />
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select label="Template" value={editTemplateKey} onChange={(e) => setEditTemplateKey(e.target.value)}>
                {templates.map((t) => (
                  <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Idioma" value={editLanguage} onChange={(e) => setEditLanguage(e.target.value)} fullWidth />
            <TextField label="Tom de voz" value={editTone} onChange={(e) => setEditTone(e.target.value)} fullWidth />
            <TextField label="Estilo narrativo" value={editNarrativeStyle} onChange={(e) => setEditNarrativeStyle(e.target.value)} fullWidth />
            <TextField
              label="Restrições (JSON)"
              value={editConstraints}
              onChange={(e) => setEditConstraints(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit} disabled={loading}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Excluir roteiro</DialogTitle>
        <DialogContent>
          <Typography>Confirma excluir o roteiro #{scriptRow?.id}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={loading}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
